import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getImageUrl } from "../lib/utils";
import {
  ChevronRight,
  FileSpreadsheet,
  FileText,
  File,
  Paperclip,
  Check,
  Phone,
  Star,
  Eye,
  Download,
  ShieldCheck,
  Clock,
  BadgeCheck,
  CalendarClock,
} from "lucide-react";
import api from "../services/api";
import { useContactSettings } from "../hooks/useContactSettings";
import ConsultationModal from "../components/modals/ConsultationModal";

const getFileKind = (type = "") => {
  if (type.includes("pdf")) return "pdf";
  if (type.includes("sheet") || type.includes("excel")) return "excel";
  if (type.includes("word") || type.includes("document")) return "word";
  return "file";
};

const KIND_META = {
  pdf: { label: "PDF", icon: FileText, className: "bg-red-50 text-red-600" },
  excel: {
    label: "Excel",
    icon: FileSpreadsheet,
    className: "bg-emerald-50 text-emerald-600",
  },
  word: { label: "Word", icon: File, className: "bg-blue-50 text-blue-600" },
  file: {
    label: "File",
    icon: File,
    className: "bg-admin-slate-100 text-admin-slate-600",
  },
};

const TABS = [
  { key: "description", label: "Description" },
  { key: "specifications", label: "Specifications" },
];

const ClinicPackageDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("description");
  const [isConsultModalOpen, setIsConsultModalOpen] = useState(false);
  const { contactSettings } = useContactSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);
    setActiveTab("description");
    api
      .get(`/clinic-setup-packages/${id}`)
      .then((response) => {
        if (response.data.success) {
          setPkg(response.data.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const enquiryPhone = contactSettings?.phone?.replace(/[^\d+]/g, "");

  if (loading) {
    return (
      <div className="w-full bg-white">
        <div className="container mx-auto px-4 py-10 animate-pulse">
          <div className="h-4 w-64 bg-gray-100 rounded mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="h-80 sm:h-[420px] bg-gray-100 rounded-2xl" />
            <div className="space-y-4">
              <div className="h-4 w-24 bg-gray-100 rounded" />
              <div className="h-8 w-2/3 bg-gray-100 rounded" />
              <div className="h-8 w-1/3 bg-gray-100 rounded" />
              <div className="h-24 w-full bg-gray-100 rounded" />
              <div className="h-12 w-full bg-gray-100 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !pkg) {
    return (
      <div className="w-full bg-white">
        <div className="container mx-auto px-4 py-24 text-center">
          <h1 className="text-xl font-heading font-bold text-[#1B365D] mb-2">
            Package not found
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            This setup package may have been removed or is no longer active.
          </p>
          <button
            onClick={() => navigate("/clinic-setup")}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#1B365D] text-white hover:bg-[#152a4a] transition-colors"
          >
            Back to Setup Packages
          </button>
        </div>
      </div>
    );
  }

  const discountPercent = Number(pkg.discountPercent) || 0;
  const hasDiscount = discountPercent > 0;
  const price = Number(pkg.price) || 0;
  const originalPrice = hasDiscount ? Math.round(price / (1 - discountPercent / 100)) : null;
  const saveAmount = hasDiscount ? originalPrice - price : 0;
  const primaryDoc = pkg.documents?.[0];
  const primaryDocMeta = primaryDoc ? KIND_META[getFileKind(primaryDoc.type)] : null;
  const PrimaryDocIcon = primaryDocMeta?.icon;
  const visibleTabs = TABS;

  return (
    <div className="w-full bg-white pb-20 md:pb-0">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
          <Link to="/" className="hover:text-gray-700 transition-colors">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link
            to="/clinic-setup"
            className="hover:text-gray-700 transition-colors"
          >
            New Clinic Setup
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-secondary-600 font-medium truncate max-w-[200px]">
            {pkg.name}
          </span>
        </nav>
      </div>

      <div className="container mx-auto px-4 py-6 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Media column - prioritizes package images, falls back to document icon.
              Stretches to match the info column's height on desktop so the two
              columns stay level regardless of how much content (e.g. resources)
              the info column has. */}
          <div className="lg:h-full">
            <div className="relative rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50 group h-full">
              {(pkg.images?.[0] || pkg.image) ? (
                <div className="w-full h-80 sm:h-[420px] lg:h-full bg-white">
                  <img
                    src={getImageUrl(pkg.images?.[0] || pkg.image)}
                    alt={pkg.name}
                    className="w-full h-full object-contain sm:object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              ) : primaryDoc ? (
                <div
                  className={`w-full h-80 sm:h-[420px] lg:h-full flex flex-col items-center justify-center gap-3 px-8 py-8 text-center overflow-y-auto ${primaryDocMeta?.className || 'bg-gray-100 text-gray-500'}`}
                >
                  {PrimaryDocIcon && <PrimaryDocIcon className="w-16 h-16 flex-shrink-0" strokeWidth={1.5} />}
                  <span className="text-sm font-semibold break-words">
                    {primaryDoc.name}
                  </span>
                  {pkg.documents?.length > 1 && (
                    <span className="text-xs text-gray-500">
                      +{pkg.documents.length - 1} more file
                      {pkg.documents.length - 1 > 1 ? "s" : ""} below
                    </span>
                  )}
                </div>
              ) : (
                <div className="w-full h-80 sm:h-[420px] lg:h-full bg-admin-slate-50 flex flex-col items-center justify-center gap-3">
                  <span className="text-6xl font-bold text-gray-300 opacity-50">{pkg.name?.charAt(0)}</span>
                </div>
              )}

              {pkg.type && (
                <span className="absolute top-4 left-4 inline-flex items-center text-2xs font-bold uppercase tracking-wide bg-white/95 backdrop-blur text-[#1B365D] rounded-full px-3 py-1.5 shadow-sm">
                  {pkg.type}
                </span>
              )}

              {pkg.isFeatured && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-2xs font-bold uppercase bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-full px-3 py-1.5 shadow-sm">
                  <Star className="w-3 h-3" fill="currentColor" strokeWidth={0} />
                  Most Popular
                </span>
              )}

              {hasDiscount && (
                <div className="absolute -top-2 -left-2 z-10 w-16 h-16 flex items-center justify-center drop-shadow-md">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-rose-500 to-red-600" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 rotate-45" />
                  <span className="relative text-white text-xs font-extrabold leading-none text-center">
                    {discountPercent}%<br />OFF
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Info column */}
          <div className="flex flex-col">
            {pkg.type && (
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
                {pkg.type} Package
              </span>
            )}

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-3">
              {pkg.name}
            </h1>

            {/* Price */}
            <div className="mb-4">
              {hasDiscount && (
                <div className="text-gray-400 line-through text-xs sm:text-sm mb-0.5">
                  Rs. {originalPrice.toLocaleString()}
                </div>
              )}
              <div className="flex items-center flex-wrap gap-2.5">
                <span className="text-3xl font-bold text-[#1B365D]">
                  Rs. {price.toLocaleString()}
                </span>
                {pkg.priceNote && (
                  <span className="text-sm text-gray-500">{pkg.priceNote}</span>
                )}
                {hasDiscount && (
                  <span className="bg-red-50 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full">
                    SAVE Rs.{saveAmount.toLocaleString()} | {discountPercent}% OFF
                  </span>
                )}
              </div>
            </div>

            {pkg.description && (
              <p className="text-sm text-gray-600 leading-relaxed mb-5">
                {pkg.description}
              </p>
            )}

            {/* Trust row */}
            <div className="flex items-center flex-wrap gap-x-3 gap-y-2 mb-6 pb-6 border-b border-gray-100">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-green-500" strokeWidth={2} />
                <span className="text-xs text-[#2c5282] font-medium whitespace-nowrap">
                  Genuine Equipment
                </span>
              </div>
              <div className="w-px h-3.5 bg-gray-300" />
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-blue-500" strokeWidth={2} />
                <span className="text-xs text-[#2c5282] font-medium whitespace-nowrap">
                  Fast Response
                </span>
              </div>
              <div className="w-px h-3.5 bg-gray-300" />
              <div className="flex items-center gap-1.5">
                <BadgeCheck className="w-4 h-4 text-[#0a2540]" strokeWidth={2} />
                <span className="text-xs text-[#2c5282] font-medium whitespace-nowrap">
                  Trusted Setup Partner
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:items-start w-full mt-2">
              <button
                onClick={() => setIsConsultModalOpen(true)}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-8 py-3.5 text-sm font-semibold shadow-sm transition-all hover:-translate-y-0.5 w-full sm:w-auto ${pkg.isFeatured
                    ? "bg-secondary-600 text-white hover:bg-secondary-700 shadow-secondary-200"
                    : "bg-[#1B365D] text-white hover:bg-[#152a4a] shadow-blue-900/20"
                  }`}
              >
                <CalendarClock className="w-5 h-5" strokeWidth={2} />
                Book Free Consultation
              </button>

              <div className="mt-3 w-full sm:w-auto flex justify-center sm:justify-start pl-2">
                <span className="text-xs text-gray-500 flex items-center gap-1.5">
                  Or call us directly at
                  <a href={enquiryPhone ? `tel:${enquiryPhone}` : "#"} className="font-semibold text-[#1B365D] hover:underline flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {contactSettings?.phone || enquiryPhone || "our support"}
                  </a>
                </span>
              </div>

              {/* Downloadable Resources - right below the CTAs so they're
                  visible to every user without needing to dig through tabs.
                  The image column stretches (lg:h-full above) to match this
                  column's height, so adding resources here never leaves
                  blank space under the image. */}
              {pkg.documents?.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-100 w-full">
                  <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">
                    <Paperclip className="w-3.5 h-3.5" strokeWidth={2} />
                    Downloadable Resources
                  </h3>
                  <div className="space-y-2">
                    {pkg.documents.map((doc, i) => {
                      const kind = getFileKind(doc.type);
                      const meta = KIND_META[kind];
                      const DocIcon = meta.icon;
                      return (
                        <div
                          key={doc.id || doc.url || i}
                          className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:border-secondary-200 hover:shadow-sm transition-all"
                        >
                          <span
                            className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg ${meta.className}`}
                          >
                            <DocIcon className="w-5 h-5" strokeWidth={2} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {doc.name}
                            </p>
                            <p className="text-xs text-gray-400">{meta.label} document</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View"
                              className="p-2 rounded-lg text-gray-400 hover:text-secondary-600 hover:bg-secondary-50 transition-colors"
                            >
                              <Eye className="w-4 h-4" strokeWidth={2} />
                            </a>
                            <a
                              href={doc.url}
                              download
                              title="Download"
                              className="p-2 rounded-lg text-gray-400 hover:text-secondary-600 hover:bg-secondary-50 transition-colors"
                            >
                              <Download className="w-4 h-4" strokeWidth={2} />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs - Description / Specifications */}
        <div className="border-t border-gray-200 mt-8 scroll-mt-12">
          <div className="flex overflow-x-auto border-b border-gray-200 scrollbar-hide">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-2 px-2 sm:py-3 sm:px-4 text-xs sm:text-sm font-medium whitespace-nowrap ${activeTab === tab.key
                    ? "text-secondary-600 border-b-2 border-secondary-500"
                    : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-3 sm:p-6">
            {activeTab === "description" && (
              <div className="space-y-6">
                <div className="prose prose-sm max-w-none text-sm text-gray-700">
                  {pkg.description ? (
                    <p>{pkg.description}</p>
                  ) : (
                    <p className="text-gray-400">No description available.</p>
                  )}
                </div>

                {pkg.features?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3">
                      What's Included
                    </h3>
                    <ul className="space-y-2.5">
                      {pkg.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2.5 text-sm text-gray-700"
                        >
                          <span className="mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50">
                            <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === "specifications" && (
              <div className="overflow-hidden">
                {pkg.specifications?.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <tbody className="divide-y divide-gray-200 text-xs sm:text-sm text-gray-700">
                      {pkg.specifications.map((spec, i) => (
                        <tr key={i}>
                          <td className="py-2 sm:py-3 w-1/3 font-medium text-gray-900">
                            {spec.key}
                          </td>
                          <td className="py-2 sm:py-3">{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-400">
                    No specifications added for this package.
                  </p>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 p-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] md:hidden">
        <button
          onClick={() => setIsConsultModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 rounded-xl py-3 bg-[#1B365D] text-white font-semibold text-sm shadow-lg shadow-blue-900/20"
        >
          <CalendarClock className="w-5 h-5" />
          Book Free Consultation
        </button>
      </div>

      <ConsultationModal
        isOpen={isConsultModalOpen}
        onClose={() => setIsConsultModalOpen(false)}
        pkg={pkg}
      />
    </div>
  );
};

export default ClinicPackageDetailPage;
